import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, ShieldCheck, Scan } from "lucide-react";
import { Button } from "../ui/button";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] },
};

export default function Hero() {
  return (
    <section className="relative soft-gradient overflow-hidden">
      <div className="absolute inset-0 dotgrid opacity-40" />
      <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-sky-200/40 blur-3xl" />
      <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-emerald-200/40 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-24 md:pt-24 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* LEFT */}
          <div className="lg:col-span-7">
            <motion.div
              {...fadeUp}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 backdrop-blur px-3 py-1.5 text-xs font-mono-num tracking-wider text-slate-600"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <span>v1.0 · CNN + DenseNet121 + SwinViT</span>
              <Sparkles className="h-3 w-3 text-sky-500" />
            </motion.div>

            <motion.h1
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="font-heading mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light text-slate-900 leading-[1.02]"
            >
              AI-Powered <br />
              <span className="text-slate-400">Dermatology</span>{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Assistant.</span>
                <span className="absolute left-0 right-0 bottom-2 h-3 bg-sky-200/70 -z-0 rounded-sm" />
              </span>
            </motion.h1>

            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.2 }}
              className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl"
            >
              Detect skin diseases in seconds with a hybrid deep-learning engine
              orchestrated by a multi-agent validation system. Built for clinicians
              who value <span className="text-slate-900 font-medium">speed</span>,{" "}
              <span className="text-slate-900 font-medium">accuracy</span> and{" "}
              <span className="text-slate-900 font-medium">explainability</span>.
            </motion.p>

            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.3 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link to="/register">
                <Button
                  size="lg"
                  className="rounded-full bg-slate-900 hover:bg-slate-800 text-white px-7 h-12 group"
                  data-testid="hero-upload-btn"
                >
                  Upload Image
                  <ArrowUpRight className="h-4 w-4 ml-1 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <a href="#demo">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full border-slate-300 bg-white/70 backdrop-blur h-12 px-7 text-slate-700 hover:bg-white cursor-pointer"
                  data-testid="hero-demo-btn"
                >
                  Try Demo
                </Button>
              </a>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.4 }}
              className="mt-10 flex items-center gap-6 text-xs font-mono-num tracking-wider text-slate-500 uppercase"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-slate-400" />
                HIPAA-minded
              </div>
              <div className="h-3 w-px bg-slate-300" />
              <div className="flex items-center gap-2">
                <Scan className="h-4 w-4 text-slate-400" />
                ISIC reference set
              </div>
              <div className="h-3 w-px bg-slate-300" />
              <div>&lt; 2s inference</div>
            </motion.div>
          </div>

          {/* RIGHT — floating cards */}
          <div className="lg:col-span-5 relative min-h-[460px]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="absolute right-0 top-0 w-[320px] glass rounded-2xl p-5 animate-floaty"
              data-testid="hero-prediction-card"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Prediction</div>
                  <div className="font-heading text-xl text-slate-900 mt-1">Melanocytic Nevus</div>
                </div>
                <div className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-mono-num px-2 py-1">
                  BENIGN
                </div>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                  <span>Confidence</span>
                  <span className="font-mono-num text-slate-800">94.7%</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: "94.7%" }}
                    transition={{ duration: 1.4, delay: 0.6 }}
                    className="h-full bg-gradient-to-r from-sky-400 to-emerald-400"
                  />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] font-mono-num tracking-wider text-slate-500">
                <div className="rounded-lg bg-slate-50 p-2">
                  <div>IoU</div>
                  <div className="text-slate-900 text-sm mt-0.5">0.94</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-2">
                  <div>AGENTS</div>
                  <div className="text-slate-900 text-sm mt-0.5">4/4</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-2">
                  <div>TIME</div>
                  <div className="text-slate-900 text-sm mt-0.5">1.2s</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="absolute left-0 top-40 w-[250px] glass rounded-2xl p-4 animate-floaty-slow"
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Agent Collaboration</div>
              <ul className="space-y-2 text-[11px] text-slate-700">
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                  Vision Agent segmented lesion
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Classifier reached consensus
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-indigo-300" />
                  Validator cross-checked ISIC
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="absolute right-6 bottom-4 glass rounded-2xl p-4 flex items-center gap-3"
            >
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-sky-200 to-emerald-200 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-slate-900" />
              </div>
              <div>
                <div className="text-sm font-medium text-slate-900">88.7% Validation</div>
                <div className="text-[10px] text-slate-500 font-mono-num tracking-wider">ACC · +6.4% WITH AGENTS</div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
